/**
 * Field calibration presets — mirror of /app/backend/modules/field_presets.py.
 *
 * We bundle the catalogue with the frontend so the Wizard dropdown renders
 * instantly without an extra round-trip. The server is still the source of
 * truth: it validates every preset id submitted to /api/sequences/.../calibrate
 * and /api/tactics/{id}/calibrate against this same list. Keep the two files
 * in sync when adding/removing presets.
 *
 * `length` runs along the long axis of the field (goalline-to-goalline),
 * `width` along the short axis (sideline-to-sideline). The 4 image clicks
 * must always be supplied in canonical order:
 *   1) Top-Left  2) Top-Right  3) Bottom-Right  4) Bottom-Left
 * where "Top" = the edge near the back-line of the endzone the player is
 * attacking and "Bottom" = the opposite edge.
 *
 * `pt0PtAxis` declares which physical dimension the FIRST click-edge
 * (pt0→pt1) spans. Full-field-style presets click along the back sideline
 * first (= length axis), so `"length"`. Endzone-style presets click along
 * the goalline first (= width axis), so `"width"`. The flag is forwarded
 * to `/calibrate` so the homography solver picks the correct destination
 * rectangle — see backend `modules/calibrator.PT0_PT1_AXIS_*` for the
 * full rationale (and the 2026-02-25 bug fix that surfaced the issue).
 */
export const FIELD_PRESETS = {
  Outdoor: [
    {
      id: "hauptspielfeld",
      labelDe: "Hauptspielfeld",
      labelEn: "Full Field",
      length: 100.0, width: 37.0,
      pt0PtAxis: "length",
      hint: "All 4 outer corners of the entire pitch.",
      status: "stable",
    },
    {
      id: "endzone",
      labelDe: "Endzone",
      labelEn: "Endzone",
      length: 18.0, width: 37.0,
      pt0PtAxis: "width",
      hint: "Back-line and goal-line corners of one endzone.",
      status: "stable",
    },
    {
      id: "central_play",
      labelDe: "Spielfläche (Goalline → Goalline)",
      labelEn: "Playing Field (Goal-to-Goal)",
      length: 64.0, width: 37.0,
      pt0PtAxis: "length",
      hint: "Goal line to goal line — no endzones.",
      status: "draft",
    },
    {
      id: "endzone_plus_brick",
      labelDe: "Endzone + Brick (Backline → Brick-Linie)",
      labelEn: "Endzone + Brick Zone",
      length: 38.0, width: 37.0,
      pt0PtAxis: "width",
      hint: "Back of endzone to the brick mark (20 m past goal line).",
      status: "draft",
    },
    {
      id: "half_central_play",
      labelDe: "Halbes Feld (Mittellinie → Goalline)",
      labelEn: "Half Field (Midline → Goal)",
      length: 32.0, width: 37.0,
      pt0PtAxis: "length",
      hint: "Midline of the playing area to one goal line.",
      status: "draft",
    },
    {
      id: "brick_zone",
      labelDe: "Brick-Zone (Goalline → Brick)",
      labelEn: "Brick Zone (Goal Line → Brick)",
      length: 20.0, width: 37.0,
      pt0PtAxis: "width",
      hint: "Goal line to brick mark — best when only an endzone area is in frame.",
      status: "draft",
    },
    // ── 🆕 Phase E v8c.2 · Multi-Zone-Capable Presets ────────────────
    {
      id: "goalline_to_midfield_left",
      labelDe: "Goalline → Mittellinie (Linke Hälfte)",
      labelEn: "Goal Line → Midfield (Left)",
      length: 32.0, width: 37.0,
      pt0PtAxis: "length",
      requiresSide: true,
      hint: "LEFT goal-line to the midfield line.",
      status: "draft",
    },
    {
      id: "goalline_to_midfield_right",
      labelDe: "Goalline → Mittellinie (Rechte Hälfte)",
      labelEn: "Goal Line → Midfield (Right)",
      length: 32.0, width: 37.0,
      // 🐛 v8c.23 · war "length" — WIDTH-Modus nötig, damit der Brick
      // seitenbewusst berechnet wird (Backend-Spiegel).
      pt0PtAxis: "width",
      requiresSide: true,
      hint: "Midfield to the RIGHT goal-line.",
      status: "draft",
    },
    {
      id: "brick_to_midfield_left",
      labelDe: "Brick → Mittellinie (Linke Hälfte)",
      labelEn: "Brick → Midfield (Left)",
      length: 12.0, width: 37.0,
      pt0PtAxis: "length",
      requiresSide: true,
      hint: "LEFT brick mark to the midfield line.",
      status: "draft",
    },
    {
      id: "brick_to_midfield_right",
      labelDe: "Brick → Mittellinie (Rechte Hälfte)",
      labelEn: "Brick → Midfield (Right)",
      length: 12.0, width: 37.0,
      // 🐛 v8c.23 · s. goalline_to_midfield_right.
      pt0PtAxis: "width",
      requiresSide: true,
      hint: "Midfield line to the RIGHT brick mark.",
      status: "draft",
    },
    {
      id: "brick_to_brick_rect",
      labelDe: "Brick → Brick (Zentrale Box)",
      labelEn: "Brick → Brick (Central Box)",
      length: 24.0, width: 37.0,
      pt0PtAxis: "length",
      clickInstructions: [
        "1: Own Brick Left Corner",
        "2: Own Brick Right Corner",
        "3: Far Brick Right Corner",
        "4: Far Brick Left Corner",
      ],
      hint: "Central spatial box between both brick marks.",
      status: "draft",
    },
    // ── 🆕 Phase E v8c.4 · LINIEN (2 clicks each) ────────────────────
    {
      id: "line_goalline_left", kind: "line",
      labelDe: "Linke Goalline (Querlinie)",
      labelEn: "Left Goal Line",
      length: 0, width: 37, pt0PtAxis: "width",
      hint: "Querlinie · 2 Klicks (Start + End der sichtbaren Linie).",
      status: "draft",
    },
    {
      id: "line_goalline_right", kind: "line",
      labelDe: "Rechte Goalline (Querlinie)",
      labelEn: "Right Goal Line",
      length: 0, width: 37, pt0PtAxis: "width",
      hint: "Querlinie · 2 Klicks (Start + End der sichtbaren Linie).",
      status: "draft",
    },
    {
      id: "line_backline_left", kind: "line",
      labelDe: "Linke Backline (Querlinie)",
      labelEn: "Left Back Line",
      length: 0, width: 37, pt0PtAxis: "width",
      hint: "Querlinie · 2 Klicks (Start + End der sichtbaren Linie).",
      status: "draft",
    },
    {
      id: "line_backline_right", kind: "line",
      labelDe: "Rechte Backline (Querlinie)",
      labelEn: "Right Back Line",
      length: 0, width: 37, pt0PtAxis: "width",
      hint: "Querlinie · 2 Klicks (Start + End der sichtbaren Linie).",
      status: "draft",
    },
    {
      id: "line_midfield", kind: "line",
      labelDe: "Mittellinie (Querlinie)",
      labelEn: "Midfield Line",
      length: 0, width: 37, pt0PtAxis: "width",
      hint: "Querlinie · 2 Klicks (Start + End der sichtbaren Linie).",
      status: "draft",
    },
    {
      id: "line_sideline_top_full", kind: "line",
      labelDe: "Längslinie OBEN (komplett)",
      labelEn: "Top Sideline (full)",
      length: 100, width: 0, pt0PtAxis: "length",
      hint: "Längslinie Zuschauer-Seite · 2 Klicks komplette Linie.",
      status: "draft",
    },
    {
      id: "line_sideline_top_left_half", kind: "line",
      labelDe: "Längslinie OBEN (linke Hälfte)",
      labelEn: "Top Sideline (left half)",
      length: 50, width: 0, pt0PtAxis: "length",
      hint: "Längslinie Zuschauer-Seite · linke Hälfte (2 Klicks).",
      status: "draft",
    },
    {
      id: "line_sideline_top_right_half", kind: "line",
      labelDe: "Längslinie OBEN (rechte Hälfte)",
      labelEn: "Top Sideline (right half)",
      length: 50, width: 0, pt0PtAxis: "length",
      hint: "Längslinie Zuschauer-Seite · rechte Hälfte (2 Klicks).",
      status: "draft",
    },
    {
      id: "line_sideline_bottom_full", kind: "line",
      labelDe: "Längslinie UNTEN (komplett)",
      labelEn: "Bottom Sideline (full)",
      length: 100, width: 0, pt0PtAxis: "length",
      hint: "Längslinie Bench-Seite · 2 Klicks komplette Linie.",
      status: "draft",
    },
    {
      id: "line_sideline_bottom_left_half", kind: "line",
      labelDe: "Längslinie UNTEN (linke Hälfte)",
      labelEn: "Bottom Sideline (left half)",
      length: 50, width: 0, pt0PtAxis: "length",
      hint: "Längslinie Bench-Seite · linke Hälfte (2 Klicks).",
      status: "draft",
    },
    {
      id: "line_sideline_bottom_right_half", kind: "line",
      labelDe: "Längslinie UNTEN (rechte Hälfte)",
      labelEn: "Bottom Sideline (right half)",
      length: 50, width: 0, pt0PtAxis: "length",
      hint: "Längslinie Bench-Seite · rechte Hälfte (2 Klicks).",
      status: "draft",
    },
    // 🆕 Phase E v8c.9 (2026-02-28) · Goalline-to-Goalline sideline segments.
    // For wide-angle / high-vantage shots where the camera captures the
    // FULL length of one sideline between the two goal-lines (excludes
    // both endzones). U17 length = L − 2E = 64 − 20 = 44 m.
    {
      id: "line_sideline_top_goal_to_goal", kind: "line",
      labelDe: "Längslinie OBEN · Goal → Goal",
      labelEn: "Top Sideline · Goalline → Goalline",
      length: 64, width: 0, pt0PtAxis: "length",
      hint: "Längslinie Zuschauer-Seite · LINKE Goalline → RECHTE Goalline (volle Spielzone, ohne Endzonen).",
      status: "draft",
    },
    {
      id: "line_sideline_bottom_goal_to_goal", kind: "line",
      labelDe: "Längslinie UNTEN · Goal → Goal",
      labelEn: "Bottom Sideline · Goalline → Goalline",
      length: 64, width: 0, pt0PtAxis: "length",
      hint: "Längslinie Bench-Seite · LINKE Goalline → RECHTE Goalline (volle Spielzone, ohne Endzonen).",
      status: "draft",
    },
    // ── 🆕 Phase E v8c.6 · Partial-Sideline SEGMENT presets ──────────
    // Profile-aware short line segments. zoneWorldPoints below resolves
    // each one using the active fieldProfile (L/W/E/B), so the lengths
    // shown here are display-only Adult-defaults — real coords adapt
    // per match profile.
    {
      id: "line_brick_to_brick_top", kind: "line",
      labelDe: "Brick → Brick · OBEN (Längs-Segment)",
      labelEn: "Brick to Brick · TOP sideline",
      length: 24, width: 0, pt0PtAxis: "length",
      hint: "Längs-Segment auf der Zuschauer-Seite zwischen den beiden Brick-Projektionen.",
      status: "draft",
    },
    {
      id: "line_brick_to_brick_bottom", kind: "line",
      labelDe: "Brick → Brick · UNTEN (Längs-Segment)",
      labelEn: "Brick to Brick · BOTTOM sideline",
      length: 24, width: 0, pt0PtAxis: "length",
      hint: "Längs-Segment auf der Bench-Seite zwischen den beiden Brick-Projektionen.",
      status: "draft",
    },
    {
      id: "line_endzone_depth_top_left", kind: "line",
      labelDe: "Endzone-Tiefe · OBEN LINKS",
      labelEn: "Endzone depth · TOP LEFT",
      length: 18, width: 0, pt0PtAxis: "length",
      hint: "Endzone-Tiefe entlang der oberen Längslinie · linke Seite.",
      status: "draft",
    },
    {
      id: "line_endzone_depth_top_right", kind: "line",
      labelDe: "Endzone-Tiefe · OBEN RECHTS",
      labelEn: "Endzone depth · TOP RIGHT",
      length: 18, width: 0, pt0PtAxis: "length",
      hint: "Endzone-Tiefe entlang der oberen Längslinie · rechte Seite.",
      status: "draft",
    },
    {
      id: "line_endzone_depth_bottom_left", kind: "line",
      labelDe: "Endzone-Tiefe · UNTEN LINKS",
      labelEn: "Endzone depth · BOTTOM LEFT",
      length: 18, width: 0, pt0PtAxis: "length",
      hint: "Endzone-Tiefe entlang der unteren Längslinie · linke Seite.",
      status: "draft",
    },
    {
      id: "line_endzone_depth_bottom_right", kind: "line",
      labelDe: "Endzone-Tiefe · UNTEN RECHTS",
      labelEn: "Endzone depth · BOTTOM RIGHT",
      length: 18, width: 0, pt0PtAxis: "length",
      hint: "Endzone-Tiefe entlang der unteren Längslinie · rechte Seite.",
      status: "draft",
    },
    {
      id: "line_goalline_to_brick_top_left", kind: "line",
      labelDe: "Goalline → Brick · OBEN LINKS",
      labelEn: "Goalline → Brick · TOP LEFT",
      length: 20, width: 0, pt0PtAxis: "length",
      hint: "Längs-Segment Goalline → Brick (linke Hälfte, Zuschauer-Seite).",
      status: "draft",
    },
    {
      id: "line_goalline_to_brick_bottom_left", kind: "line",
      labelDe: "Goalline → Brick · UNTEN LINKS",
      labelEn: "Goalline → Brick · BOTTOM LEFT",
      length: 20, width: 0, pt0PtAxis: "length",
      hint: "Längs-Segment Goalline → Brick (linke Hälfte, Bench-Seite).",
      status: "draft",
    },
    {
      id: "line_goalline_to_brick_top_right", kind: "line",
      labelDe: "Goalline → Brick · OBEN RECHTS",
      labelEn: "Goalline → Brick · TOP RIGHT",
      length: 20, width: 0, pt0PtAxis: "length",
      hint: "Längs-Segment Brick → Goalline (rechte Hälfte, Zuschauer-Seite).",
      status: "draft",
    },
    {
      id: "line_goalline_to_brick_bottom_right", kind: "line",
      labelDe: "Goalline → Brick · UNTEN RECHTS",
      labelEn: "Goalline → Brick · BOTTOM RIGHT",
      length: 20, width: 0, pt0PtAxis: "length",
      hint: "Längs-Segment Brick → Goalline (rechte Hälfte, Bench-Seite).",
      status: "draft",
    },
    // ── 🆕 Phase E v8c.4 · PUNKTE (1 double-click each) ──────────────
    {
      id: "point_brick_left", kind: "point",
      labelDe: "Brick-Marke LINKS (Punkt)",
      labelEn: "Brick Mark LEFT",
      length: 0, width: 0, pt0PtAxis: "length",
      hint: "Punkt · Doppelklick auf das gemalte X / den Cone der LINKEN Brick-Marke.",
      status: "draft",
    },
    {
      id: "point_brick_right", kind: "point",
      labelDe: "Brick-Marke RECHTS (Punkt)",
      labelEn: "Brick Mark RIGHT",
      length: 0, width: 0, pt0PtAxis: "length",
      hint: "Punkt · Doppelklick auf das gemalte X / den Cone der RECHTEN Brick-Marke.",
      status: "draft",
    },
  ],
  Beach: [
    { id: "hauptspielfeld", labelDe: "Hauptspielfeld", labelEn: "Full Field",
      length: 75.0, width: 25.0, pt0PtAxis: "length",
      hint: "All 4 outer corners.", status: "stable" },
    { id: "endzone", labelDe: "Endzone", labelEn: "Endzone",
      length: 10.0, width: 25.0, pt0PtAxis: "width",
      hint: "Corners of one beach endzone.", status: "stable" },
    { id: "central_play", labelDe: "Spielfläche (Goalline → Goalline)",
      labelEn: "Playing Field (Goal-to-Goal)",
      length: 55.0, width: 25.0, pt0PtAxis: "length",
      hint: "Goal line to goal line.", status: "draft" },
    { id: "half_central_play", labelDe: "Halbes Feld (Mittellinie → Goalline)",
      labelEn: "Half Field (Midline → Goal)",
      length: 27.5, width: 25.0, pt0PtAxis: "length",
      hint: "Midline to one goal line.", status: "draft" },
  ],
  Indoor: [
    { id: "hauptspielfeld", labelDe: "Hauptspielfeld", labelEn: "Full Field",
      length: 50.0, width: 25.0, pt0PtAxis: "length",
      hint: "All 4 outer corners.", status: "stable" },
    { id: "endzone", labelDe: "Endzone", labelEn: "Endzone",
      length: 5.0, width: 25.0, pt0PtAxis: "width",
      hint: "Corners of one indoor endzone.", status: "stable" },
    { id: "central_play", labelDe: "Spielfläche (Goalline → Goalline)",
      labelEn: "Playing Field (Goal-to-Goal)",
      length: 40.0, width: 25.0, pt0PtAxis: "length",
      hint: "Goal line to goal line.", status: "draft" },
    { id: "half_central_play", labelDe: "Halbes Feld (Mittellinie → Goalline)",
      labelEn: "Half Field (Midline → Goal)",
      length: 20.0, width: 25.0, pt0PtAxis: "length",
      hint: "Midline to one goal line.", status: "draft" },
  ],
};

/** Look up a preset by (format, id). Falls back to Outdoor[0]. */
export function getPreset(format, id) {
  const list = FIELD_PRESETS[format] || FIELD_PRESETS.Outdoor;
  return list.find((p) => p.id === id)
    || FIELD_PRESETS.Outdoor.find((p) => p.id === id)
    || list[0];
}

/** Outer field rectangle (Full Field) for the chosen format — used by the
 *  top-down preview overlay so users see the calibrated sub-rectangle inside
 *  the full pitch silhouette. */
export function fullFieldDims(format) {
  return getPreset(format, "hauptspielfeld");
}

/**
 * 🆕 Phase E v8c · 2026-02-28 · Per-Profile Calibration-Rect Scaling.
 *
 * Resolve the calibration RECTANGLE dimensions (length, width) for a
 * given preset against the ACTIVE field profile — not the hardcoded
 * Adult/Beach/Indoor defaults in ``FIELD_PRESETS``. This is the math
 * surface for the "U17 short-distance bug": a U17 field is 64×25 m,
 * its endzone is 10×25 m, but the hard-coded "endzone" preset was
 * 18×37 m (Adult), so the homography was solved against the wrong
 * destination rectangle and every projected length came out scaled
 * by ~1.5-1.8×.
 *
 * Strategy: derive each preset's rect dims from the profile dims:
 *   • hauptspielfeld    → (L, W)
 *   • endzone           → (E, W)
 *   • endzone_plus_brick→ (E + B, W)
 *   • central_play      → (L − 2E, W)
 *   • half_central_play → ((L − 2E) / 2, W)
 *   • brick_zone        → (B, W)
 *
 * Where:
 *   L = profile.field_length_m
 *   W = profile.field_width_m
 *   E = profile.endzone_depth_m
 *   B = profile.brick_distance_m
 *
 * Falls back to the legacy hard-coded preset values when no profile
 * is supplied (keeps unit tests + the wizard's first-render before
 * Step1 game_config arrives working).
 */
export function presetRectDimsFor(format, presetId, fieldProfile) {
  const fallback = getPreset(format, presetId);
  if (!fieldProfile || typeof fieldProfile !== "object") {
    return { length: fallback.length, width: fallback.width };
  }
  const L = Number(fieldProfile.field_length_m) || fallback.length;
  const W = Number(fieldProfile.field_width_m) || fallback.width;
  const E = Number(fieldProfile.endzone_depth_m) || (fallback.length === 18 ? 18 : 18);
  const B = Number(fieldProfile.brick_distance_m) || 20;

  switch (presetId) {
    case "hauptspielfeld":
      return { length: L, width: W };
    case "endzone":
      return { length: E, width: W };
    case "endzone_plus_brick":
      return { length: E + B, width: W };
    case "central_play":
      return { length: Math.max(1, L - 2 * E), width: W };
    case "half_central_play":
      return { length: Math.max(1, (L - 2 * E) / 2), width: W };
    case "brick_zone":
      return { length: B, width: W };
    // 🆕 Phase E v8c.2 · Multi-zone-capable presets. Dims derive
    // from the active field profile so a U17 (64×25) "goalline →
    // midfield" zone becomes (L/2 − E) × W = (32 − 18 = 14) × 25 m.
    case "goalline_to_midfield_left":
    case "goalline_to_midfield_right":
      return { length: Math.max(1, L / 2 - E), width: W };
    case "brick_to_midfield_left":
    case "brick_to_midfield_right":
      return { length: Math.max(1, L / 2 - E - B), width: W };
    case "brick_to_brick_rect":
      return { length: Math.max(1, L - 2 * (E + B)), width: W };
    default:
      return { length: fallback.length, width: fallback.width };
  }
}


/**
 * 🆕 Phase E v8c.8 (2026-02-28) · Per-Profile dropdown-label resolver
 * for ALL preset kinds (area + line + point). Replaces the static
 * `${p.length}×${p.width} m` strings in the AddZonePanel dropdown
 * with values that adapt to the active match's `game_config`.
 *
 * • area  → `"{L}×{W} m"`   (e.g. `"10×25 m"` for U17 endzone)
 * • line  → `"{S} m"`       (1D segment length; e.g. `"24 m"` for
 *                            brick→brick top on U17)
 * • point → `"—"`           (markers have no metric length)
 *
 * Falls back to the static registry values when fieldProfile is
 * absent (e.g. before Step 1 game_config has loaded).
 */
export function presetDisplayDimsFor(format, presetId, fieldProfile) {
  const preset = getPreset(format, presetId);
  const kind = preset?.kind || "area";
  if (kind === "point") return "—";
  if (kind === "line") {
    // Derive the segment's 1D length from the world-coord endpoints
    // — guarantees profile-aware values across every line preset.
    const pts = zoneWorldPoints(format, presetId, fieldProfile, null);
    if (pts?.length === 2) {
      const [a, b] = pts;
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const len = Math.hypot(dx, dy);
      if (len > 0) return `${Math.round(len)} m`;
    }
    // Fallback: static length from registry.
    return preset.length > 0 ? `${preset.length} m` : "—";
  }
  // area
  const { length, width } = presetRectDimsFor(format, presetId, fieldProfile);
  if (length > 0 && width > 0) {
    const fmt = (v) => (Number.isInteger(v) ? v : v.toFixed(1));
    return `${fmt(length)}×${fmt(width)} m`;
  }
  return "—";
}

/**
 * 🆕 Phase E v8c.2 · Multi-Zone World-Offset resolver (frontend mirror
 * of `backend/modules/field_presets.world_offset_for`).
 *
 * Returns the (origin_x, origin_y) where the zone's local (0, 0)
 * corner sits inside the global full-field frame, plus the resolved
 * (length, width) of the zone in metres. Used by the wizard's
 * top-down preview overlay to render every stacked zone in its
 * correct relative position on the full-pitch silhouette.
 *
 *   ((origin_x, origin_y), (zone_length, zone_width))
 *
 * The `side` argument toggles LEFT/RIGHT placement for presets that
 * have a `requiresSide` flag.
 */
export function worldOffsetFor(format, presetId, fieldProfile, side) {
  const L = Number(fieldProfile?.field_length_m) || fullFieldDims(format).length;
  const W = Number(fieldProfile?.field_width_m)  || fullFieldDims(format).width;
  const E = Number(fieldProfile?.endzone_depth_m) || 18;
  const B = Number(fieldProfile?.brick_distance_m) || 20;
  const s = (side || "").toLowerCase();

  switch (presetId) {
    case "hauptspielfeld":
      return { origin: [0, 0], dims: [L, W] };
    case "endzone":
      return {
        origin: s === "right" ? [L - E, 0] : [0, 0],
        dims: [E, W],
      };
    case "endzone_plus_brick":
      return {
        origin: s === "right" ? [L - E - B, 0] : [0, 0],
        dims: [E + B, W],
      };
    case "brick_zone":
      return {
        origin: s === "right" ? [L - E - B, 0] : [E, 0],
        dims: [B, W],
      };
    case "central_play":
      return { origin: [E, 0], dims: [Math.max(1, L - 2 * E), W] };
    case "half_central_play": {
      const half = Math.max(1, (L - 2 * E) / 2);
      return {
        origin: s === "right" ? [E + half, 0] : [E, 0],
        dims: [half, W],
      };
    }
    case "goalline_to_midfield_left":
      return { origin: [E, 0], dims: [Math.max(1, L / 2 - E), W] };
    case "goalline_to_midfield_right":
      return { origin: [L / 2, 0], dims: [Math.max(1, L / 2 - E), W] };
    case "brick_to_midfield_left":
      return { origin: [E + B, 0], dims: [Math.max(1, L / 2 - E - B), W] };
    case "brick_to_midfield_right":
      return { origin: [L / 2, 0], dims: [Math.max(1, L / 2 - E - B), W] };
    case "brick_to_brick_rect":
      return { origin: [E + B, 0], dims: [Math.max(1, L - 2 * (E + B)), W] };
    default:
      return { origin: [0, 0], dims: [L, W] };
  }
}

/**
 * 🆕 Phase E v8c.5 · Frontend mirror of `backend/modules/field_presets.zone_world_corners`.
 *
 * Returns every world-coord ([x, y] in metres on the canonical
 * full-pitch grid) the operator's clicks should map to, in the
 * SAME order the wizard collects them. Length depends on the
 * preset kind:
 *   • area  → 4×2 (corners CCW)
 *   • line  → 2×2 (start, end)
 *   • point → 1×2 (single landmark)
 *
 * Used by the top-down silhouette overlay + the coverage-score
 * helper so both render against the SAME geometry the backend
 * solver consumes.
 */
export function zoneWorldPoints(format, presetId, fieldProfile, side) {
  const L = Number(fieldProfile?.field_length_m) || fullFieldDims(format).length;
  const W = Number(fieldProfile?.field_width_m)  || fullFieldDims(format).width;
  const E = Number(fieldProfile?.endzone_depth_m) || 18;
  const B = Number(fieldProfile?.brick_distance_m) || 20;
  const preset = (() => {
    try { return getPreset(format, presetId); }
    catch { return null; }
  })();
  const kind = preset?.kind || "area";

  if (kind === "point") {
    if (presetId === "point_brick_left")  return [[E + B, W / 2]];
    if (presetId === "point_brick_right") return [[L - E - B, W / 2]];
    return [[L / 2, W / 2]];
  }
  if (kind === "line") {
    switch (presetId) {
      case "line_goalline_left":  return [[E, 0], [E, W]];
      case "line_goalline_right": return [[L - E, 0], [L - E, W]];
      case "line_backline_left":  return [[0, 0], [0, W]];
      case "line_backline_right": return [[L, 0], [L, W]];
      case "line_midfield":       return [[L / 2, 0], [L / 2, W]];
      case "line_sideline_top_full":         return [[0, W], [L, W]];
      case "line_sideline_top_left_half":    return [[0, W], [L / 2, W]];
      case "line_sideline_top_right_half":   return [[L / 2, W], [L, W]];
      case "line_sideline_bottom_full":      return [[0, 0], [L, 0]];
      case "line_sideline_bottom_left_half": return [[0, 0], [L / 2, 0]];
      case "line_sideline_bottom_right_half":return [[L / 2, 0], [L, 0]];
      // 🆕 v8c.9 · Goalline-to-goalline sideline segments.
      case "line_sideline_top_goal_to_goal":    return [[E, W], [L - E, W]];
      case "line_sideline_bottom_goal_to_goal": return [[E, 0], [L - E, 0]];
      // 🆕 v8c.6 · Partial-sideline SEGMENT presets — profile-aware.
      case "line_brick_to_brick_top":        return [[E + B, W], [L - E - B, W]];
      case "line_brick_to_brick_bottom":     return [[E + B, 0], [L - E - B, 0]];
      case "line_endzone_depth_top_left":    return [[0, W], [E, W]];
      case "line_endzone_depth_top_right":   return [[L - E, W], [L, W]];
      case "line_endzone_depth_bottom_left": return [[0, 0], [E, 0]];
      case "line_endzone_depth_bottom_right":return [[L - E, 0], [L, 0]];
      case "line_goalline_to_brick_top_left":     return [[E, W], [E + B, W]];
      case "line_goalline_to_brick_bottom_left":  return [[E, 0], [E + B, 0]];
      case "line_goalline_to_brick_top_right":    return [[L - E - B, W], [L - E, W]];
      case "line_goalline_to_brick_bottom_right": return [[L - E - B, 0], [L - E, 0]];
      default: return [[0, W / 2], [L, W / 2]];
    }
  }
  // AREA — corners CCW starting at the origin offset.
  const { origin: [ox, oy], dims: [zl, zw] } = worldOffsetFor(format, presetId, fieldProfile, side);
  return [
    [ox,         oy],
    [ox + zl,    oy],
    [ox + zl,    oy + zw],
    [ox,         oy + zw],
  ];
}

